import React, { useState, Dispatch, SetStateAction, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";
import { fetchUsers, fetchGroups } from "components/Drawer/drawerApi";
import { useMsal } from "@azure/msal-react";
export const NEXUS_API_BASE_URL_V2 = process.env.REACT_APP_NEXUS_API_BASE_URL;
import { ANALYTICS_ON_HPC_FILTER_OPTION, NEXUS_API_BASE_URL } from "const";
import { getAccessToken } from "config/token";
import { IUser, IGroup } from "components/Drawer/types";
import axios from "axios";
import {
  Modal,
  Form,
  Row,
  Col,
  Radio,
  Button,
  AutoComplete,
  Spin,
  Select,
} from "antd";
import { commonProject, commonStudy } from "components/Drawer/constants";
import { PROFILE_TYPE_LABELS, EXCLUDED_FILTER_PROFILE_TYPES } from "utils/normalizeSpaces";


function getProfileTypeOptions() {
  const options = Object.entries(PROFILE_TYPE_LABELS)
    .filter(([value]) => !EXCLUDED_FILTER_PROFILE_TYPES.includes(value))
    .map(([value, label]) => ({ label, value }));
  if (ANALYTICS_ON_HPC_FILTER_OPTION == "false") {
    return options.filter(opt => opt.value !== "analytics_hpc");
  }
  return options;
}

interface FilterModalProps {
  visible: boolean;
  onClose: () => void; 
  resetSignal: number;
  selectedProfileTypes: string[];
  onApply: (filters: Record<string, any>) => void;
  setSelectedProfileTypes: Dispatch<SetStateAction<string[]>>;
}

interface IStudyProject {
  study_id: string;
  study_name: string;
  project_id: string;
  project_name: string;
}

const FilterModal: React.FC<FilterModalProps> = ({
  visible,
  onClose,
  onApply,
  resetSignal,
  selectedProfileTypes,
  setSelectedProfileTypes,
}) => {
  
  const [form] = Form.useForm();
  const [owners, setOwners] = useState<{ user_email: string; access_level: string }[]>([]);
  const [ownerOptions, setOwnerOptions] = useState<{ label: string; value: string }[]>([]);
  const [fetchingOwners, setFetchingOwners] = useState<boolean>(false);
  const [allData, setAllData] = useState<IStudyProject[]>([]);
  const [loading, setLoading] = useState(false);

  const [regionOptions] = useState<string[]>([
    "eu-central-1",
    "us-east-1",
    "ap-southeast-1",
  ]);
  const [studyOptions, setStudyOptions] = useState<{ label: string; value: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ label: string; value: string }[]>([]);


  const [selectedStudies, setSelectedStudies] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);

  const [groupOptions, setGroupOptions] = useState<{ label: string; value: string }[]>([]);
  const [fetchingGroups, setFetchingGroups] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  const [selectedClassifications, setSelectedClassifications] = useState<string[]>([]);
  const [selectedIsActive, setSelectedIsActive] = useState<string[]>([]);
  const [selectedProjectTypes, setSelectedProjectTypes] = useState<string[]>([]);

  const msalInstance = useMsal(); // Import and use the actual msalInstance

  const removeDuplicates = <T, K extends keyof any>(data: T[], getId: (item: T) => K): T[] => {
    const seen = new Set<K>();
    return data.filter((item) => {
      const id = getId(item);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };


  useEffect(() => {
    if (visible) {
      const fetchAll = async () => {
        try {
          setLoading(true);
          const token = await getAccessToken(msalInstance);
          const response = await axios.get(`${NEXUS_API_BASE_URL}/nexus/default?region=`, {
            headers: { Authorization: `Bearer ${token?.token}` },
          });

          // Adapt to response shape: response.data.data.{projects,studies}
          const apiData = response?.data;
          const projectsArr: { project_id: string; project_name: string }[] = apiData?.projects || [];
          const studiesArr: {
            study_id: string;
            study_name: string | null;
            project_id: string;
            project_name: string;
          }[] = apiData?.studies || [];

          // Build unified list (projects without study info get empty strings for study fields)
          const unified: IStudyProject[] = [
            {
              study_id: commonStudy.study_id,
              study_name: commonStudy.study_name,
              project_id: commonStudy.project_id,
              project_name: commonStudy.project_name,
            },
            // Add common project
            {
              study_id: "",
              study_name: "",
              project_id: commonProject.project_id,
              project_name: commonProject.project_name,
            },
            ...projectsArr.map((p) => ({
              study_id: "",
              study_name: "",
              project_id: p.project_id,
              project_name: p.project_name,
            })),
            ...studiesArr.map((s) => ({
              study_id: s.study_id,
              study_name: s.study_name || "",
              project_id: s.project_id,
              project_name: s.project_name,
            })),
          ];

          setAllData(unified);
        } catch (err) {
          console.error("Error fetching studies/projects", err);
          setAllData([]);
        } finally {
          setLoading(false);
        }
      };
      fetchAll();
    }
  }, [visible, form]);

  // Initialize first 12 study & project options (deduplicated) once allData loads
  useEffect(() => {
    if (allData.length) {
      const initialStudyOpts = removeDuplicates(
        allData.filter((d) => d.study_id),
        (d) => d.study_id,
      )
        .slice(0, 12)
        .map((item) => ({
          label: item.study_name
            ? `${item.study_name} (${item.study_id})`
            : item.study_id,
          value: item.study_name
            ? `${item.study_name} (${item.study_id})`
            : item.study_id,
        }));

      const initialProjectOpts = removeDuplicates(allData, (d) => d.project_id)
        .slice(0, 12)
        .map((item) => ({
          label: `${item.project_name} (${item.project_id})`,
          value: `${item.project_name} (${item.project_id})`,
        }));

      setStudyOptions(initialStudyOpts);
      setProjectOptions(initialProjectOpts);
    } else {
      setStudyOptions([]);
      setProjectOptions([]);
    }
  }, [allData]);


  const debouncedOwnerSearch = useDebouncedCallback(
    (search: string): void => {
      if (search.length >= 3) {
        setFetchingOwners(true);
        fetchUsers(msalInstance, search).then((users: IUser[]) => {
          const fetchedOwners = users.map((user) => ({
            label: user.mail,
            value: String(user.id), // Ensure id is converted to string
          }));
          setOwnerOptions(fetchedOwners);
        });
        setFetchingOwners(false);
        setFetchingOwners(false);
      } else {
        setOwnerOptions([]);
      }
    },
    800,
  );

  const debouncedUnifiedSearch = useDebouncedCallback((search: string): void => {
    const term = search.trim().toLowerCase();

    if (!term) {
      const resetStudies = removeDuplicates(
        allData.filter((d) => d.study_id),
        (d) => d.study_id,
      )
        .slice(0, 12)
        .map((item) => ({
          label: item.study_name
            ? `${item.study_name} (${item.study_id})`
            : item.study_id,
          value: item.study_name
            ? `${item.study_name} (${item.study_id})`
            : item.study_id,
        }));

      const resetProjects = removeDuplicates(allData, (d) => d.project_id)
        .slice(0, 12)
        .map((item) => ({
          label: `${item.project_name} (${item.project_id})`,
          value: `${item.project_name} (${item.project_id})`,
        }));

      setStudyOptions(resetStudies);
      setProjectOptions(resetProjects);
      return;
    }

    // For studies: only search study_name and study_id
    const studyFiltered = allData.filter((item) => {
      if (!item.study_id) return false;
      return (
        (item.study_name && item.study_name.toLowerCase().includes(term)) ||
        item.study_id.toLowerCase().includes(term)
      );
    });

    // For projects: only search project_name and project_id
    const projectFiltered = allData.filter((item) => {
      return (
        item.project_name.toLowerCase().includes(term) ||
        item.project_id.toLowerCase().includes(term)
      );
    });

    const studyOpts = removeDuplicates(studyFiltered, (d) => d.study_id)
      .slice(0, 12)
      .map((item) => ({
        label: item.study_name
          ? `${item.study_name} (${item.study_id})`
          : item.study_id,
        value: item.study_name
          ? `${item.study_name} (${item.study_id})`
          : item.study_id,
      }));

    const projectOpts = removeDuplicates(projectFiltered, (d) => d.project_id)
      .slice(0, 12)
      .map((item) => ({
        label: `${item.project_name} (${item.project_id})`,
        value: `${item.project_name} (${item.project_id})`,
      }));

    setStudyOptions(studyOpts);
    setProjectOptions(projectOpts);
  }, 400);

  const debouncedGroupSearch = useDebouncedCallback((search: string): void => {
    if (search.length >= 2) {
      setFetchingGroups(true);
      fetchGroups(msalInstance, search)
        .then((groups: IGroup[]) => {
          const formattedGroups = groups.map((g) => ({
            label: g.name,
            value: g.name,
          }));
          setGroupOptions(formattedGroups);
        })
        .catch((err) => {
          console.error("Error fetching groups:", err);
        })
        .finally(() => {
          setFetchingGroups(false);
        });
    } else {
      setGroupOptions([]);
    }
  }, 600);

  const handleReset = () => {
    setOwners([]);
    form.resetFields();
    setSelectedProjectTypes([]); // Reset project types
    setSelectedProjects([]);
    setSelectedProfileTypes([]);
    setSelectedClassifications([]);
    setSelectedIsActive([]);
    setSelectedStudies([]);
    setSelectedRegions([]);
    setSelectedGroups([]);
    form.setFieldsValue({
      bucketTypeFilter: undefined,
      region: undefined,
      expires_on: undefined,
      study_id: undefined,
      project_id: undefined,
      group_filter: undefined,
      licensing: undefined,
      owner: undefined,
      pagination: undefined,
      space_name_sort: undefined,
    });
  };

  
  useEffect(() => {
    handleReset();
  }, [resetSignal]);
  
  return (
    <Modal
      title="Filter Options"
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="reset" onClick={handleReset}>
          Reset
        </Button>,
        <Button
          key="apply"
          type="primary"
          onClick={() => {
             // Helper to extract ID from "Name (ID)" format
            const extractId = (str: string): string => {
              const match = str.match(/\(([^)]+)\)$/);
              return match ? match[1] : str;
            };

            const formValues = form.getFieldsValue();

             // Extract only IDs from selected studies and projects
            const studyIds = selectedStudies.length > 0 
              ? selectedStudies.map(extractId) 
              : undefined;
            
            const projectIds = selectedProjects.length > 0 
              ? selectedProjects.map(extractId) 
              : undefined;


            const filters: Record<string, any> = {
              bucket_type_filter: formValues.bucket_type_filter,
              licensing_obligation_filter: formValues.licensing_obligation_filter,
              region: selectedRegions.length > 0 ? selectedRegions : undefined,
              study_id: studyIds,
              project_id: projectIds,
              group_filter: selectedGroups.length > 0 ? selectedGroups : undefined,
              owner_filter: owners.length > 0 ? owners.map((owner) => owner.user_email) : undefined,
              projectType: selectedProjectTypes.length > 0 ? selectedProjectTypes : undefined, // Separate state
              profileType: selectedProfileTypes.length > 0 ? selectedProfileTypes : undefined,
              classification_filter: selectedClassifications.length > 0 ? selectedClassifications : undefined,
              is_active_filter: selectedIsActive.length > 0 ? selectedIsActive : undefined,
              space_name_sort: formValues.space_name_sort || undefined,
              region_name_sort: formValues.region_name_sort || undefined,
            };
            onApply(filters);
            onClose();
          }}
        >
          Apply
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Row gutter={[16, 16]}>

          <Col span={12}>
            <Form.Item name="space_name_sort" label="Space Name Sort">
              <Radio.Group>
                <Radio value="asc">Ascending</Radio>
                <Radio value="desc">Descending</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          {/* Region */}
          <Col span={12}>
            <Form.Item name="region_name_sort" label="Region">
              <Radio.Group>
                <Radio value="asc">Ascending</Radio>
                <Radio value="desc">Descending</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>



          {/* GxP / Non-GxP */}
          <Col span={12}>
            <Form.Item name="bucket_type_filter" label="Bucket Type">
              <Radio.Group>
                <Radio value="gxp">GxP</Radio>
                <Radio value="nongxp">Non-GxP</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          {/* Licensing Obligation */}
          <Col span={12}>
            <Form.Item name="licensing_obligation_filter" label="Licensing Obligation">
              <Radio.Group>
                <Radio value="yes">Yes</Radio>
                <Radio value="no">No</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="project_type" label="Project Type">
              <Select
                mode="multiple"
                placeholder="Select project types"
                value={selectedProjectTypes}
                onChange={(selectedValues) => {
                  setSelectedProjectTypes(selectedValues);
                }}
                options={[
                  { label: "Analytics", value: "analytics" },
                  { label: "Data", value: "data" },
                  { label: "DSE App", value: "dse_app" },
                ]}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          {/* Profile Type */}
          <Col span={12}>
            <Form.Item name="profile_type" label="Profile Type">
              <Select
                mode="multiple"
                placeholder="Select profile types"
                value={selectedProfileTypes}
                onChange={(selectedValues) => {
                  setSelectedProfileTypes(selectedValues);
                }}
                options={getProfileTypeOptions()}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          {/* Study ID */}
          <Col span={12}>
            <Form.Item
              name="study_id"
              label="Study ID"
              rules={[{ required: false, message: "Please enter a study ID." }]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder="Search and select studies"
                value={selectedStudies}
                onSearch={(value) => debouncedUnifiedSearch(value)}
                onChange={(selectedValues) => {
                  setSelectedStudies(selectedValues);
                }}
                filterOption={false}
                notFoundContent={loading ? "Loading..." : "No results found"}
                options={studyOptions}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          {/* Project ID */}
          <Col span={12}>
            <Form.Item
              name="project_id"
              label="Project ID"
              rules={[{ required: false, message: "Please enter a project ID." }]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder="Search and select projects"
                value={selectedProjects}
                onSearch={(value) => debouncedUnifiedSearch(value)}
                onChange={(selectedValues) => {
                  setSelectedProjects(selectedValues);
                }}
                filterOption={false}
                notFoundContent={loading ? "Loading..." : "No results found"}
                options={projectOptions}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          {/* Group Filter */}
          <Col span={12}>
            <Form.Item
              name="group_filter"
              label="Group Filter"
              rules={[{ required: false, message: "Please enter a group name." }]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder="Search and select groups"
                value={selectedGroups}
                onSearch={(value) => debouncedGroupSearch(value)}
                onChange={(selectedValues) => {
                  setSelectedGroups(selectedValues);
                }}
                filterOption={false}
                notFoundContent={fetchingGroups ? <Spin size="small" /> : "No results found"}
                options={groupOptions}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="region" label="Region">
              <Select
                mode="multiple"
                placeholder="Select regions"
                value={selectedRegions}
                onChange={(vals) => setSelectedRegions(vals)}
                options={regionOptions.map(r => ({ label: r, value: r }))}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>



          <Col span={12}>
            <Form.Item name="classification_filter" label="Classification">
              <Select
                mode="multiple"
                placeholder="Select classifications"
                value={selectedClassifications}
                onChange={(selectedValues) => {
                  setSelectedClassifications(selectedValues);
                }}
                options={[
                  { label: "Internal", value: "internal" },
                  { label: "Restricted", value: "restricted" },
                  { label: "Secret", value: "secret" },
                ]}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          {/* Is Active Filter */}
          <Col span={12}>
            <Form.Item name="is_active_filter" label="Status">
              <Select
                mode="multiple"
                placeholder="Select status"
                value={selectedIsActive}
                onChange={(selectedValues) => {
                  setSelectedIsActive(selectedValues);
                }}
                options={[
                  { label: "Active", value: "1" },
                  { label: "Inactive", value: "0" },
                  { label: "Provisioning", value: "-1" },
                ]}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          {/* Owners Section */}
          <Col span={24}>
            <Form.Item
              name="owners"
              label="Owners"
              rules={[
                {
                  validator: (_, owners) => {
                    if (Array.isArray(owners) && owners.length > 0) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Please select at least one owner."));
                  },
                },
              ]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder="Search and select owners"
                value={owners.map((owner) => owner.user_email)}
                onSearch={(value) => debouncedOwnerSearch(value)}
                onChange={(selectedEmails) => {
                  const updatedOwners = selectedEmails.map((email) => {
                    // Preserve access_level if user already exists in state
                    const existing = owners.find((o) => o.user_email === email);
                    return {
                      user_email: email,
                      access_level: existing?.access_level ?? "", // default to empty string
                    };
                  });
                  setOwners(updatedOwners);
                }}
                filterOption={false}
                notFoundContent={fetchingOwners ? "Loading..." : "No results found"}
                options={ownerOptions.map((option) => ({
                  label: option.label,
                  value: option.label,
                }))}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default FilterModal;
